import { Box, Grid2, Typography } from '@mui/material'
import { useAuth } from 'components/AuthProvider/AuthProvider'
import Button from 'components/Button'
import DefaultLayout from 'components/layout/DefaultLayout'
import VerificationInput from 'components/VerificationInput'
import { useFormik } from 'formik'
import { useEffect } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import * as Yup from 'yup'
import useStyles, { wrapperContentStyles } from './styles'

interface FormValues {
  otp: string
}

const initialValues: FormValues = {
  otp: ''
}

const validationSchema = Yup.object().shape({
  otp: Yup.string()
    .required('Verify code is required')
    .min(6, 'Must be exactly 6 digits')
    .max(6, 'Must be exactly 6 digits')
})

export default function VerifyOtp(): JSX.Element {
  const { classes } = useStyles()
  const { verifyOtp, loading, error: verifyError } = useAuth()

  const recaptchaEnabled = JSON.parse(
    process.env.REACT_APP_RECAPTCHA_ENABLED || 'true'
  )
  const { executeRecaptcha } = useGoogleReCaptcha()

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values: FormValues) => handleVerifyOtp(values)
  })

  const { errors, touched, handleSubmit, setValues, setErrors, setTouched } =
    formik

  useEffect(() => {
    setErrors({
      otp: verifyError ?? undefined
    })
  }, [loading, verifyError, setErrors])

  const handleChange = (code: string) => {
    setValues({
      otp: code
    })
    setErrors({ otp: undefined })
    setTouched({ otp: false })
  }

  const handleBlur = () => {
    setTouched({ otp: true })
  }

  const handleVerifyOtp = async (values: FormValues) => {
    if (!executeRecaptcha && recaptchaEnabled) return
    if (errors.otp) return

    const recaptchaToken =
      recaptchaEnabled && executeRecaptcha
        ? await executeRecaptcha('sessions/create')
        : undefined

    verifyOtp({
      mfaCode: values.otp,
      email: '',
      session: '',
      challengeName: '',
      recaptcha_token: recaptchaToken
    })
  }

  return (
    <DefaultLayout
      center
      wrapperContent={wrapperContentStyles}
      heading='Verify Otp'
    >
      <Box className={classes.container}>
        <form noValidate onSubmit={handleSubmit}>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12 }}>
              <Typography align='center'>
                Enter the code generated by your auth app below
              </Typography>
            </Grid2>
            <Grid2 className={classes.verificationInputRow} size={{ xs: 12 }}>
              <VerificationInput
                data-testid='verification-input'
                validChars='0-9'
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Grid2>
            <Grid2 className={classes.errorRow} size={{ xs: 12 }}>
              {errors?.otp && touched?.otp && (
                <Box className={classes.errorText}>{errors.otp}</Box>
              )}
            </Grid2>
            <Grid2 className={classes.submitRow} size={{ xs: 12 }}>
              <Button
                fullWidth
                color='primary'
                type='submit'
                disabled={loading}
              >
                Verify
              </Button>
            </Grid2>
          </Grid2>
        </form>
      </Box>
    </DefaultLayout>
  )
}
